import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete,
	Box,
	Chip,
	InputAdornment,
	InputBase,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
	comparePayCodesForDisplay,
	formatPayCodeOptionLabel,
	PayCodeResponse,
} from '../../types/Payroll/PayCodes'

export type LaborRevenueRateChange = {
	payCodeId?: number | null
	markup?: string
	amount?: string
}

type RateHourlyUnitPriceCellProps = {
	unitPrice: string
	markup: string
	payCodeId: number | null
	payCodes: PayCodeResponse[]
	payCodesLoading: boolean
	onChange: (change: LaborRevenueRateChange) => void
	readOnly?: boolean
}

const isAmountText = (value: string): boolean => /^\d*\.?\d*$/.test(value.trim())

const formatPayCodeLabel = (payCode: PayCodeResponse): string => {
	const label = formatPayCodeOptionLabel(payCode)
	return payCode.skillCode ? `${label} · ${payCode.skillCode}` : label
}

const formatRate = (rate: number | null | undefined): string => (rate != null ? `$${rate}` : '—')

const formatPayCodeRates = (payCode: PayCodeResponse): string =>
	[
		`ST ${formatRate(payCode.stRate)}`,
		`OT ${formatRate(payCode.otRate)}`,
		`DT ${formatRate(payCode.dtRate)}`,
		`TT ${formatRate(payCode.ttRate)}`,
	].join(' · ')

const formatUnitPrice = (unitPrice: string): string => {
	const n = Number(unitPrice)
	return unitPrice.trim() !== '' && Number.isFinite(n) ? `$${n.toFixed(2)}` : '$—'
}

export default function RateHourlyUnitPriceCell({
	unitPrice,
	markup,
	payCodeId,
	payCodes,
	payCodesLoading,
	onChange,
	readOnly = false,
}: RateHourlyUnitPriceCellProps) {
	const theme = useTheme()
	const [amountText, setAmountText] = useState(unitPrice)
	const [isEditing, setIsEditing] = useState(false)

	useEffect(() => {
		if (!isEditing) setAmountText(payCodeId == null ? unitPrice : '')
	}, [unitPrice, payCodeId, isEditing])

	const sortedPayCodes = useMemo(() => [...payCodes].sort(comparePayCodesForDisplay), [payCodes])

	const isManualAmount = payCodeId == null && unitPrice.trim() !== ''

	const fieldBg =
		theme.palette.mode === 'dark'
			? alpha(theme.palette.common.white, 0.04)
			: alpha(theme.palette.common.black, 0.02)

	const markupField = (
		<Tooltip
			title={
				isManualAmount
					? 'A typed amount is used as-is, without markup'
					: 'Markup applied to the classification ST rate'
			}
			placement='top'
			enterDelay={600}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 0.25,
					flex: '0 0 88px',
					width: 88,
					px: 0.75,
					borderRadius: 1,
					backgroundColor: fieldBg,
					border: '1px solid transparent',
					opacity: isManualAmount ? 0.5 : 1,
					transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {
						duration: theme.transitions.duration.shorter,
					}),
					'&:focus-within': {
						backgroundColor: theme.palette.background.paper,
						borderColor: theme.palette.primary.main,
						boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
					},
				}}
			>
				<Typography component='span' variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
					%
				</Typography>
				<InputBase
					value={isManualAmount ? '' : markup}
					disabled={readOnly || isManualAmount}
					onChange={(e) => {
						if (isAmountText(e.target.value)) onChange({ markup: e.target.value })
					}}
					placeholder='Markup'
					inputProps={{
						'aria-label': 'Labor Revenue markup percent',
						inputMode: 'decimal',
					}}
					sx={{
						flex: 1,
						'& .MuiInputBase-input': {
							fontSize: '0.875rem',
							py: 0.75,
							px: 0.25,
							textAlign: 'right',
							fontVariantNumeric: 'tabular-nums',
						},
					}}
				/>
			</Box>
		</Tooltip>
	)

	if (payCodeId != null) {
		const payCode = payCodes.find((code) => code.id === payCodeId)
		const isRemoved = !payCodesLoading && !payCode
		const name = payCode ? payCode.skillCode || payCode.classification : 'Classification removed'
		const title = payCode
			? `${formatPayCodeLabel(payCode)}: ${formatPayCodeRates(payCode)}. Unit price = ST rate × markup %.`
			: 'This classification was removed from pay codes.'

		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Tooltip title={title} placement='top' enterDelay={400}>
					<Chip
						size='small'
						variant='outlined'
						color={isRemoved ? 'warning' : 'primary'}
						label={`${name} · ${formatUnitPrice(unitPrice)}`}
						aria-label={`Classification ${name}, unit price ${formatUnitPrice(unitPrice)}`}
						onDelete={readOnly ? undefined : () => onChange({ payCodeId: null })}
						sx={{ maxWidth: '100%', fontWeight: 600 }}
					/>
				</Tooltip>
				{markupField}
			</Box>
		)
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Autocomplete<PayCodeResponse, false, false, true>
				freeSolo
				disabled={readOnly}
				openOnFocus
				size='small'
				loading={payCodesLoading}
				options={sortedPayCodes}
				value={null}
				inputValue={amountText}
				getOptionLabel={(option) => (typeof option === 'string' ? option : formatPayCodeLabel(option))}
				filterOptions={(options, state) => {
					const query = state.inputValue.trim().toLowerCase()
					if (isAmountText(query)) return options
					return options.filter((option) => formatPayCodeLabel(option).toLowerCase().includes(query))
				}}
				onInputChange={(_, value, reason) => {
					if (reason === 'clear') {
						setAmountText('')
						onChange({ amount: '' })
						return
					}
					if (reason !== 'input') return
					setAmountText(value)
					if (isAmountText(value)) onChange({ amount: value.trim() })
				}}
				onChange={(_, value) => {
					if (!value || typeof value === 'string') return
					onChange({ payCodeId: value.id })
				}}
				onFocus={() => setIsEditing(true)}
				onBlur={() => setIsEditing(false)}
				renderOption={(props, option) => (
					<li {...props} key={option.id}>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant='body2' noWrap>
								{formatPayCodeLabel(option)}
							</Typography>
							<Typography variant='caption' color='text.secondary'>
								{formatPayCodeRates(option)}
							</Typography>
						</Box>
					</li>
				)}
				componentsProps={{ popper: { style: { width: 340 }, placement: 'bottom-start' } }}
				sx={{
					flex: '1 1 140px',
					minWidth: 140,
					maxWidth: 200,
					'& .MuiOutlinedInput-root': { backgroundColor: fieldBg, fontSize: '0.875rem' },
					'& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
					'& .MuiOutlinedInput-input': { textAlign: 'right' },
				}}
				renderInput={(params) => (
					<Tooltip
						title='Pick a classification, or type an amount (used without markup)'
						placement='top'
						enterDelay={600}
					>
						<TextField
							{...params}
							variant='outlined'
							placeholder='Amount or class'
							InputProps={{
								...params.InputProps,
								startAdornment: <InputAdornment position='start'>$</InputAdornment>,
							}}
							inputProps={{
								...params.inputProps,
								inputMode: 'decimal',
								'aria-label': 'Labor Revenue unit price or classification',
							}}
						/>
					</Tooltip>
				)}
			/>
			{markupField}
		</Box>
	)
}
